import { Card as ChakraCard } from "@chakra-ui/react";
import type { AccentRole } from "theme/accents";
import { density as densityScale, type Density } from "theme/spacing";

/**
 * Soft-editorial card variants.
 *
 *  - tone="flat"     (default) → 1px hairline border, no shadow. Form / list cards.
 *  - tone="elevated"           → soft shadow, no border. Hero KPI tiles.
 *  - tone="accent"             → 4px left rule + pastel wash background. Use with `accent`
 *                                to express section meaning (healthy / risk / warning / info / neutral).
 *
 * Density controls the inner padding (`density.compact | comfortable | spacious`)
 * — replacing per-page p={4}/p={5} drift with a single named knob.
 *
 * The legacy Chakra `variant` and `size` props remain for callers that already
 * use them; the new tokens layer on top via inline styles.
 */

type CardTone = "flat" | "elevated" | "accent";

interface CardCommonProps {
  tone?: CardTone;
  accent?: AccentRole;
  density?: Density;
}

interface CardProps extends CardCommonProps {
  children: React.ReactNode;
  variant?: "elevated" | "outline" | "subtle";
  size?: "sm" | "md" | "lg";
  className?: string;
  onClick?: () => void;
}

const toneStyles = (tone: CardTone, accent?: AccentRole) => {
  switch (tone) {
    case "elevated":
      return {
        boxShadow: "var(--shadow-card)",
        borderColor: "transparent",
        borderWidth: "0",
        bg: "var(--chakra-colors-bg-panel)",
      };
    case "accent": {
      const role = accent ?? "neutral";
      return {
        boxShadow: "none",
        borderLeftWidth: "4px",
        borderLeftStyle: "solid",
        borderLeftColor: `var(--accent-${role}-rule)`,
        borderTopWidth: "1px",
        borderRightWidth: "1px",
        borderBottomWidth: "1px",
        borderTopColor: "var(--card-border)",
        borderRightColor: "var(--card-border)",
        borderBottomColor: "var(--card-border)",
        borderTopStyle: "solid" as const,
        borderRightStyle: "solid" as const,
        borderBottomStyle: "solid" as const,
        bg: `var(--accent-${role}-wash)`,
      };
    }
    case "flat":
    default:
      return {
        boxShadow: "none",
        borderWidth: "1px",
        borderStyle: "solid" as const,
        borderColor: "var(--card-border)",
        bg: "var(--chakra-colors-bg-panel)",
      };
  }
};

const densityPadding = (d: Density | undefined) =>
  densityScale[d ?? "comfortable"];

export const Card = ({
  children,
  variant = "outline",
  size = "md",
  tone = "flat",
  accent,
  density,
  className,
  ...props
}: CardProps) => {
  return (
    <ChakraCard.Root
      variant={variant}
      size={size}
      className={className}
      borderRadius="var(--radius-card)"
      {...toneStyles(tone, accent)}
      {...props}
    >
      <ChakraCard.Body p={densityPadding(density)}>{children}</ChakraCard.Body>
    </ChakraCard.Root>
  );
};

interface CardRootProps
  extends CardCommonProps, React.ComponentProps<typeof ChakraCard.Root> {
  variant?: "elevated" | "outline" | "subtle";
  size?: "sm" | "md" | "lg";
}

export const CardRoot = ({
  children,
  variant = "outline",
  size = "md",
  tone = "flat",
  accent,
  className,
  ...props
}: CardRootProps) => {
  return (
    <ChakraCard.Root
      variant={variant}
      size={size}
      className={className}
      borderRadius="var(--radius-card)"
      {...toneStyles(tone, accent)}
      {...props}
    >
      {children}
    </ChakraCard.Root>
  );
};

interface CardSlotProps extends React.ComponentProps<typeof ChakraCard.Body> {
  density?: Density;
}

export const CardHeader = ({ children, density, ...props }: CardSlotProps) => {
  return (
    <ChakraCard.Header p={densityPadding(density)} {...props}>
      {children}
    </ChakraCard.Header>
  );
};

export const CardBody = ({ children, density, ...props }: CardSlotProps) => {
  return (
    <ChakraCard.Body p={densityPadding(density)} {...props}>
      {children}
    </ChakraCard.Body>
  );
};

export const CardFooter = ({ children, density, ...props }: CardSlotProps) => {
  return (
    <ChakraCard.Footer p={densityPadding(density)} {...props}>
      {children}
    </ChakraCard.Footer>
  );
};

// Compound API for backwards compatibility
Card.Root = CardRoot;
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
